import { useEffect, useState } from "react";
import { clientServer } from "duck/api/axios";
import { useTranslation } from "react-i18next";

const isViaductAiUrl = (src: string): boolean => {
  // Start with https://ai.{any tenant}.cloud.viaduct.ai{optional ":3002" port}
  // This pattern applies to local development (ai.viaduct.cloud.viaduct.ai:3002) and live deployments.
  const viaductPattern = /^https:\/\/ai\.[^.]+\.cloud\.viaduct\.ai(:\d+)?/;

  return viaductPattern.test(src);
};

const retrieveViaductImageContent = async (src: string): Promise<string> => {
  const response = await clientServer.get(src, {
    responseType: "blob",
  });

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(response.data);
  });

  return dataUrl;
};

interface UseLoadImageResponse {
  imageSrc: string;
  isLoading: boolean;
  error: string | null;
}

const useLoadImage = (src: string): UseLoadImageResponse => {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadImage = async () => {
      if (isViaductAiUrl(src)) {
        setIsLoading(true);

        try {
          const dataUrl = await retrieveViaductImageContent(src);
          setImageSrc(dataUrl);
          setIsLoading(false);
        } catch (err) {
          console.error("Failed to load Viaduct image:", err);
          setError(
            err instanceof Error ? err.message : t("common:images.load_failed")
          );
          setIsLoading(false);
        }
      } else {
        setImageSrc(src);
        setIsLoading(false);
      }
    };

    loadImage();
  }, [src, t]);

  return { imageSrc, isLoading, error };
};

interface ChatImageProps {
  alt: string;
  src: string;
}

/**
 * The ChatImage component lets us securely load image content from the AI server.
 * Attempting to load the images directly in an <img> tag fails because the auth header is missing.
 * This component loads the image content using an async request that includes the auth header.
 */
const MarkdownImage = ({ alt, src }: ChatImageProps) => {
  const { t } = useTranslation();
  const { imageSrc, isLoading, error } = useLoadImage(src);

  if (isLoading) {
    return (
      <span>
        <br />
        {t("common:images.loading")}
      </span>
    );
  }

  if (error) {
    return (
      <span>
        <br />
        {t("common:images.load_error")} {error}
      </span>
    );
  }

  return <img src={imageSrc} alt={alt} />;
};

export default MarkdownImage;
